/**
 * RTK listener that records product-usage actions on mutation success.
 * Chat message_sent is tracked from runOntologyAgentOrchestrator (SSE path).
 */
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'

import { customAgentsApi } from '../api/agents/custom-agents/custom-agents-api'
import { branchesApi } from '../api/branches/branches-api'
import { dashboardsApi } from '../api/dashboards/dashboards-api'
import { templatesApi } from '../api/dashboards/templates-api'
import { ingestApi } from '../api/ingest/ingest-api'
import { notificationRulesApi } from '../api/notification-rules/notification-rules-api'
import { trackUsageAction } from './usage-tracker'

export const usageAnalyticsBridge = createListenerMiddleware()

const configSaved = isAnyOf(
  customAgentsApi.endpoints.createCustomAgentConfig.matchFulfilled,
  customAgentsApi.endpoints.updateCustomAgentConfig.matchFulfilled,
  notificationRulesApi.endpoints.createNotificationRule.matchFulfilled,
  notificationRulesApi.endpoints.updateNotificationRule.matchFulfilled
)

usageAnalyticsBridge.startListening({
  matcher: configSaved,
  effect: () => {
    trackUsageAction('configure_agents', 'config_saved')
  }
})

const dashboardSaved = isAnyOf(
  dashboardsApi.endpoints.updateDashboardCode.matchFulfilled,
  templatesApi.endpoints.updateTemplateContent.matchFulfilled
)

usageAnalyticsBridge.startListening({
  matcher: dashboardSaved,
  effect: () => {
    trackUsageAction('dashboard_builder', 'dashboard_saved')
  }
})

const ingestStarted = isAnyOf(
  ingestApi.endpoints.beginStructuredJob.matchFulfilled,
  ingestApi.endpoints.beginAvevaGraphicsJob.matchFulfilled,
  ingestApi.endpoints.beginPlcJob.matchFulfilled,
  ingestApi.endpoints.beginRoboticsJob.matchFulfilled,
  ingestApi.endpoints.connectMTConnectAgent.matchFulfilled,
  ingestApi.endpoints.connectPI.matchFulfilled,
  ingestApi.endpoints.connectExperionErdb.matchFulfilled,
  ingestApi.endpoints.beginSrsJob.matchFulfilled,
  ingestApi.endpoints.beginSafetyManagerJob.matchFulfilled,
  ingestApi.endpoints.connectSafetyManagerDb.matchFulfilled,
  ingestApi.endpoints.beginUnstructuredJob.matchFulfilled,
  ingestApi.endpoints.beginStructuredDiffJob.matchFulfilled,
  ingestApi.endpoints.uploadStructuredDocument.matchFulfilled
)

usageAnalyticsBridge.startListening({
  matcher: ingestStarted,
  effect: () => {
    trackUsageAction('ingest', 'ingest_started')
  }
})

const ingestUploaded = isAnyOf(
  ingestApi.endpoints.uploadStructuredFile.matchFulfilled,
  ingestApi.endpoints.uploadAvevaGraphicsFile.matchFulfilled,
  ingestApi.endpoints.uploadPlcFile.matchFulfilled,
  ingestApi.endpoints.uploadRoboticsFile.matchFulfilled,
  ingestApi.endpoints.uploadSrsFile.matchFulfilled,
  ingestApi.endpoints.uploadSafetyManagerFile.matchFulfilled
)

usageAnalyticsBridge.startListening({
  matcher: ingestUploaded,
  effect: () => {
    trackUsageAction('ingest', 'ingest_uploaded')
  }
})

usageAnalyticsBridge.startListening({
  matcher: ingestApi.endpoints.uploadUnstructuredFiles.matchFulfilled,
  effect: () => {
    trackUsageAction('ingest', 'ingest_uploaded')
    trackUsageAction('ingest', 'ingest_processed')
  }
})

usageAnalyticsBridge.startListening({
  matcher: ingestApi.endpoints.applyStructuredDiff.matchFulfilled,
  effect: () => {
    trackUsageAction('ingest', 'ingest_processed')
  }
})

usageAnalyticsBridge.startListening({
  matcher: ingestApi.endpoints.answerIngestQuestion.matchFulfilled,
  effect: () => {
    trackUsageAction('ingest', 'ingest_question_answered')
  }
})

usageAnalyticsBridge.startListening({
  matcher: branchesApi.endpoints.createBranch.matchFulfilled,
  effect: () => {
    trackUsageAction('branches', 'branch_created')
  }
})

usageAnalyticsBridge.startListening({
  matcher: branchesApi.endpoints.createCommit.matchFulfilled,
  effect: () => {
    trackUsageAction('branches', 'branch_committed')
  }
})

// mergeMergeRequest fulfillment is acceptance (202 queued), not a landed merge.
// Durable apply success emits branch_merged from the merge-work terminal
// observer in cache-coherence-bridge (list/detail poll).
usageAnalyticsBridge.startListening({
  matcher: branchesApi.endpoints.mergeBranch.matchFulfilled,
  effect: (action) => {
    if (action.payload?.status === 'merged') {
      trackUsageAction('branches', 'branch_merged')
    }
  }
})
